'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'

import type { ClassData } from '@/core/types/library.type'
import type { FilterableMethods, MethodGraftabilityIndex } from '@/core/utils/filterConfig'
import {
  buildMethodGraftabilityIndex,
  collectFilterableMethods,
  computeFilterStatistics,
  getConstructorToken,
  parseFilterPatterns,
} from '@/core/utils/filterConfig'
import { analyzeLibraryGraftability, buildGgFilterCommand } from '@/core/utils/graftability'

import { NO_GRAFTABLE_METHODS_SENTINEL } from '../const'
import type { AvailableType, GraftFilterWizardData, MethodScope, UseGraftFilterWizardParams } from '../types'
import { classFqn, uniqueNonEmpty } from '../utils'

/**
 * Holds the whole state machine of the filter wizard: type/method selection, graftability
 * shortcuts, derived filter strings, preview statistics and the generated GG command. The
 * component layer only renders the values returned here.
 */
export function useGraftFilterWizard({
  open,
  libraryData,
  onOpenChange,
}: UseGraftFilterWizardParams): GraftFilterWizardData {
  const report = useMemo(() => (libraryData ? analyzeLibraryGraftability(libraryData) : null), [libraryData])

  const constructorToken = useMemo(
    () => (libraryData ? getConstructorToken(libraryData.runtimeName) : 'constructor'),
    [libraryData]
  )

  const classesByFqn = useMemo(() => {
    const map = new Map<string, ClassData>()
    for (const classData of Object.values(libraryData?.classes ?? {})) {
      map.set(classFqn(classData), classData)
    }
    return map
  }, [libraryData])

  const graftabilityIndex = useMemo<MethodGraftabilityIndex>(
    () => (libraryData ? buildMethodGraftabilityIndex(libraryData, constructorToken) : new Map()),
    [libraryData, constructorToken]
  )

  const availableTypes = useMemo<AvailableType[]>(() => {
    if (!libraryData) return []

    return Object.values(libraryData.classes ?? {}).map((classData) => {
      const fqn = classFqn(classData)
      const memberMap = graftabilityIndex.get(fqn)
      let graftable = true
      memberMap?.forEach((isGraftable) => {
        if (!isGraftable) graftable = false
      })
      return {
        fqn,
        label: classData.name,
        graftable,
      }
    })
  }, [libraryData, graftabilityIndex])

  const allTypeFqns = useMemo(() => availableTypes.map((type) => type.fqn), [availableTypes])

  const graftableTypeFqns = useMemo(
    () => availableTypes.filter((type) => type.graftable).map((type) => type.fqn),
    [availableTypes]
  )

  const [selectedTypes, setSelectedTypes] = useState<string[]>([])
  const [typePatternsText, setTypePatternsText] = useState('')
  const [methodScope, setMethodScope] = useState<MethodScope>('all')
  const [selectedMethodNames, setSelectedMethodNames] = useState<string[]>([])
  const [perTypeMethods, setPerTypeMethods] = useState<Record<string, string[]>>({})
  const [methodPatternsText, setMethodPatternsText] = useState('')
  const [targetType, setTargetType] = useState<string>('')
  const [graftableMethodsOnly, setGraftableMethodsOnly] = useState(false)
  const [graftableTypesOnly, setGraftableTypesOnly] = useState(false)

  useEffect(() => {
    if (!open || !report) return
    setSelectedTypes(report.suggestedFacadeTypes)
    setTypePatternsText('')
    setMethodScope('all')
    setSelectedMethodNames([])
    setPerTypeMethods({})
    setMethodPatternsText('')
    setTargetType(report.suggestedFacadeTypes[0] ?? '')
    setGraftableMethodsOnly(false)
    setGraftableTypesOnly(false)
  }, [open, report])

  const effectiveSelectedTypes = useMemo(() => {
    if (graftableMethodsOnly) return allTypeFqns
    if (graftableTypesOnly) return graftableTypeFqns
    return selectedTypes
  }, [graftableMethodsOnly, graftableTypesOnly, allTypeFqns, graftableTypeFqns, selectedTypes])

  useEffect(() => {
    if (effectiveSelectedTypes.length === 0) {
      setTargetType('')
      return
    }
    setTargetType((current) =>
      current && effectiveSelectedTypes.includes(current) ? current : (effectiveSelectedTypes[0] ?? '')
    )
  }, [effectiveSelectedTypes])

  const selectedTypeData = useMemo(
    () =>
      effectiveSelectedTypes.map((fqn) => classesByFqn.get(fqn)).filter((value): value is ClassData => Boolean(value)),
    [effectiveSelectedTypes, classesByFqn]
  )

  const allScopeMethods = useMemo(
    () => collectFilterableMethods(selectedTypeData, constructorToken),
    [selectedTypeData, constructorToken]
  )

  const perTypeScopeMethods = useMemo(() => {
    const classData = targetType ? classesByFqn.get(targetType) : undefined
    return collectFilterableMethods(classData ? [classData] : [], constructorToken)
  }, [targetType, classesByFqn, constructorToken])

  const typesString = useMemo(() => {
    if (graftableMethodsOnly) return uniqueNonEmpty(allTypeFqns).join(',')
    if (graftableTypesOnly) return uniqueNonEmpty(graftableTypeFqns).join(',')
    return uniqueNonEmpty([...selectedTypes, ...parseFilterPatterns(typePatternsText)]).join(',')
  }, [graftableMethodsOnly, graftableTypesOnly, allTypeFqns, graftableTypeFqns, selectedTypes, typePatternsText])

  const graftableMethodsString = useMemo(() => {
    const patterns: string[] = []
    for (const fqn of effectiveSelectedTypes) {
      const memberMap = graftabilityIndex.get(fqn)
      if (!memberMap || memberMap.size === 0) continue

      const graftableMembers: string[] = []
      let fullyGraftable = true
      memberMap.forEach((graftable, member) => {
        if (graftable) graftableMembers.push(member)
        else fullyGraftable = false
      })

      if (fullyGraftable) {
        patterns.push(`${fqn}.*`)
      } else {
        graftableMembers.forEach((member) => patterns.push(`${fqn}.${member}`))
      }
    }

    const unique = uniqueNonEmpty(patterns)
    return unique.length > 0 ? unique.join(',') : NO_GRAFTABLE_METHODS_SENTINEL
  }, [effectiveSelectedTypes, graftabilityIndex])

  const methodsString = useMemo(() => {
    if (graftableMethodsOnly) return graftableMethodsString

    const generated =
      methodScope === 'all'
        ? selectedMethodNames
        : Object.entries(perTypeMethods).flatMap(([fqn, names]) => names.map((name) => `${fqn}.${name}`))

    return uniqueNonEmpty([...generated, ...parseFilterPatterns(methodPatternsText)]).join(',')
  }, [
    graftableMethodsOnly,
    graftableMethodsString,
    methodScope,
    selectedMethodNames,
    perTypeMethods,
    methodPatternsText,
  ])

  const isMemberGraftable = useCallback(
    (name: string): boolean => {
      if (methodScope === 'all') {
        for (const classData of selectedTypeData) {
          const memberMap = graftabilityIndex.get(classFqn(classData))
          if (memberMap?.has(name) && memberMap.get(name) === false) return false
        }
        return true
      }

      const memberMap = targetType ? graftabilityIndex.get(targetType) : undefined
      return memberMap?.get(name) ?? true
    },
    [methodScope, selectedTypeData, graftabilityIndex, targetType]
  )

  const statistics = useMemo(
    () => (libraryData ? computeFilterStatistics(libraryData, typesString, methodsString) : null),
    [libraryData, typesString, methodsString]
  )

  const ggCommand = useMemo(
    () =>
      buildGgFilterCommand({
        modulePath: report?.moduleFullName ?? './your-module.jar',
        types: parseFilterPatterns(typesString),
        methods: parseFilterPatterns(methodsString),
      }),
    [report, typesString, methodsString]
  )

  const configJson = useMemo(
    () => JSON.stringify({ types: typesString, methods: methodsString }, null, 2),
    [typesString, methodsString]
  )

  const toggleType = useCallback((fqn: string, checked: boolean) => {
    setSelectedTypes((current) => {
      if (checked) return Array.from(new Set([...current, fqn]))
      return current.filter((value) => value !== fqn)
    })
  }, [])

  const toggleAllScopeMethod = useCallback((name: string, checked: boolean) => {
    setSelectedMethodNames((current) => {
      if (checked) return Array.from(new Set([...current, name]))
      return current.filter((value) => value !== name)
    })
  }, [])

  const togglePerTypeMethod = useCallback(
    (name: string, checked: boolean) => {
      if (!targetType) return
      setPerTypeMethods((current) => {
        const existing = current[targetType] ?? []
        const next = checked ? Array.from(new Set([...existing, name])) : existing.filter((value) => value !== name)

        if (next.length === 0) {
          const { [targetType]: _removed, ...rest } = current
          return rest
        }

        return { ...current, [targetType]: next }
      })
    },
    [targetType]
  )

  const handleDismiss = useCallback(() => onOpenChange(false), [onOpenChange])

  const activeMethods: FilterableMethods = methodScope === 'all' ? allScopeMethods : perTypeScopeMethods
  const targetMethodSelection = perTypeMethods[targetType] ?? []

  return {
    report,
    quickOptions: {
      graftableTypesOnly,
      graftableMethodsOnly,
      onGraftableTypesOnlyChange: setGraftableTypesOnly,
      onGraftableMethodsOnlyChange: setGraftableMethodsOnly,
    },
    types: {
      availableTypes,
      selectedTypes,
      graftableMethodsOnly,
      graftableTypesOnly,
      typePatternsText,
      onToggleType: toggleType,
      onTypePatternsChange: setTypePatternsText,
    },
    methods: {
      methodScope,
      graftableMethodsOnly,
      effectiveSelectedTypes,
      classesByFqn,
      perTypeMethods,
      targetType,
      activeMethods,
      constructorToken,
      selectedMethodNames,
      targetMethodSelection,
      methodPatternsText,
      isMemberGraftable,
      onMethodScopeChange: setMethodScope,
      onTargetTypeChange: setTargetType,
      onToggleAllScopeMethod: toggleAllScopeMethod,
      onTogglePerTypeMethod: togglePerTypeMethod,
      onMethodPatternsChange: setMethodPatternsText,
    },
    preview: statistics ? { statistics } : null,
    output: { configJson, ggCommand },
    handleDismiss,
  }
}
